import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { specialGroupApi, memberApi, SpecialGroupRequest, SpecialGroup, SpecialGroupMember, Member } from '@/api/b2b'
import { Plus, Pencil, Trash2, Users, UserPlus, UserMinus, Search, ChevronRight, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'

export default function SpecialGroupManagement() {
  const { t } = useTranslation('admin')
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<SpecialGroup | null>(null)
  const [editingGroup, setEditingGroup] = useState<{ id: number; name: string; description: string } | null>(null)
  const [formData, setFormData] = useState<SpecialGroupRequest>({ name: '', description: '' })
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [bulkProcessing, setBulkProcessing] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['special-groups'],
    queryFn: () => specialGroupApi.getAll(),
  })

  const { data: membersData } = useQuery({
    queryKey: ['members'],
    queryFn: () => memberApi.getAll(),
    enabled: isMemberModalOpen || isBulkModalOpen,
  })

  const { data: groupMembersData, isLoading: isLoadingGroupMembers } = useQuery({
    queryKey: ['special-group-members', selectedGroup?.id],
    queryFn: () => selectedGroup ? specialGroupApi.getMembers(selectedGroup.id) : null,
    enabled: !!selectedGroup,
  })

  const createMutation = useMutation({
    mutationFn: (data: SpecialGroupRequest) => specialGroupApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['special-groups'] })
      closeModal()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SpecialGroupRequest }) =>
      specialGroupApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['special-groups'] })
      closeModal()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => specialGroupApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['special-groups'] })
      setSelectedGroup(null)
    },
  })

  const addMembersMutation = useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: number; userIds: number[] }) =>
      specialGroupApi.addMembers(groupId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['special-group-members', selectedGroup?.id] })
      queryClient.invalidateQueries({ queryKey: ['special-groups'] })
      setIsMemberModalOpen(false)
      setSelectedUserIds([])
      setSearchTerm('')
    },
  })

  const removeMembersMutation = useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: number; userIds: number[] }) =>
      specialGroupApi.removeMembers(groupId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['special-group-members', selectedGroup?.id] })
      queryClient.invalidateQueries({ queryKey: ['special-groups'] })
    },
  })

  const specialGroups = data?.data?.data || []
  const allMembers = membersData?.data?.data || []
  const groupMembers = groupMembersData?.data?.data || []

  // 이미 그룹에 속한 멤버 ID 목록
  const existingMemberIds = new Set(groupMembers.map((m: SpecialGroupMember) => m.userId))

  // 추가 가능한 멤버 필터링
  const availableMembers = allMembers.filter((m: Member) => 
    !existingMemberIds.has(m.userId) &&
    (searchTerm === '' || 
      m.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const openCreateModal = () => {
    setEditingGroup(null)
    setFormData({ name: '', description: '' })
    setIsModalOpen(true)
  }

  const openEditModal = (group: SpecialGroup) => {
    setEditingGroup({ id: group.id, name: group.name, description: group.description || '' })
    setFormData({ name: group.name, description: group.description || '' })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingGroup(null)
    setFormData({ name: '', description: '' })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = (id: number, name: string) => {
    if (confirm(t('specialGroup.confirmDelete', { name }))) {
      deleteMutation.mutate(id)
    }
  }

  const toggleUserSelection = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const handleAddMembers = () => {
    if (selectedGroup && selectedUserIds.length > 0) {
      addMembersMutation.mutate({ groupId: selectedGroup.id, userIds: selectedUserIds })
    }
  }

  const handleRemoveMember = (userId: number, userName: string) => {
    if (selectedGroup && confirm(t('specialGroup.confirmRemoveMember', { name: userName }))) {
      removeMembersMutation.mutate({ groupId: selectedGroup.id, userIds: [userId] })
    }
  }

  const handleBulkUpload = async () => {
    if (!csvFile || !selectedGroup) {
      toast.warning(t('specialGroup.toast.noFile'))
      return
    }

    setBulkProcessing(true)
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length === 0) {
          toast.warning(t('specialGroup.toast.emptyFile'))
          setBulkProcessing(false)
          return
        }

        // Skip header row, extract emails
        const dataLines = lines.slice(1)
        const emails = dataLines
          .map(line => line.split(',')[0]?.trim().toLowerCase())
          .filter(email => email && email.includes('@'))

        if (emails.length === 0) {
          toast.warning(t('specialGroup.toast.noValidEmail'))
          setBulkProcessing(false)
          return
        }

        // Match emails against org members
        const emailToMember = new Map(
          allMembers.map((m: Member) => [m.email.toLowerCase(), m])
        )

        const matched: Member[] = []
        const notFound: string[] = []

        emails.forEach(email => {
          const member = emailToMember.get(email)
          if (member) {
            matched.push(member)
          } else {
            notFound.push(email)
          }
        })

        // Filter out already-in-group members
        const alreadyInGroup: string[] = []
        const toAdd = matched.filter(m => {
          if (existingMemberIds.has(m.userId)) {
            alreadyInGroup.push(m.email)
            return false
          }
          return true
        })

        if (toAdd.length === 0) {
          let msg = t('specialGroup.toast.noMembersToAdd')
          if (notFound.length > 0) {
            msg += ` ${t('specialGroup.toast.notRegistered', { emails: notFound.join(', ') })}`
          }
          if (alreadyInGroup.length > 0) {
            msg += ` ${t('specialGroup.toast.alreadyInGroup', { emails: alreadyInGroup.join(', ') })}`
          }
          toast.warning(msg)
          setBulkProcessing(false)
          return
        }

        // Call existing addMembers API
        const userIds = toAdd.map(m => m.userId)
        addMembersMutation.mutate(
          { groupId: selectedGroup.id, userIds },
          {
            onSuccess: () => {
              const message = t('specialGroup.toast.bulkSuccess', { count: toAdd.length })
              if (notFound.length > 0 || alreadyInGroup.length > 0) {
                const details: string[] = []
                if (notFound.length > 0) {
                  details.push(t('specialGroup.toast.notRegisteredCount', { count: notFound.length, emails: notFound.join(', ') }))
                }
                if (alreadyInGroup.length > 0) {
                  details.push(t('specialGroup.toast.alreadyInGroupCount', { count: alreadyInGroup.length, emails: alreadyInGroup.join(', ') }))
                }
                toast.warning(t('specialGroup.toast.bulkPartial', { count: toAdd.length, details: details.join(' / ') }))
              } else {
                toast.success(message)
              }
              setIsBulkModalOpen(false)
              setCsvFile(null)
              setBulkProcessing(false)
            },
            onError: (error: any) => {
              console.error('Bulk member add failed:', error)
              toast.error(t('specialGroup.toast.bulkError'))
              setBulkProcessing(false)
            },
          }
        )
      } catch (error: any) {
        toast.error(t('specialGroup.toast.csvParseError', { error: error.message }))
        setBulkProcessing(false)
      }
    }
    reader.readAsText(csvFile, 'UTF-8')
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('specialGroup.title')}</h1>
          <p className="mt-1 text-gray-500">{t('specialGroup.description')}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          <span>{t('specialGroup.addButton')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Special Group List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">{t('specialGroup.listTitle')}</h3>
            </div>
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
            ) : specialGroups.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {t('specialGroup.empty')}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {specialGroups.map((group) => (
                  <div
                    key={group.id}
                    onClick={() => setSelectedGroup(group)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedGroup?.id === group.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{group.name}</h4>
                        {group.description && (
                          <p className="text-sm text-gray-500 truncate mt-1">{group.description}</p>
                        )}
                        <div className="flex items-center text-sm text-gray-400 mt-2">
                          <Users className="w-4 h-4 mr-1" />
                          <span>{t('specialGroup.memberCount', { count: group.memberCount })}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Group Detail & Members */}
        <div className="lg:col-span-2">
          {selectedGroup ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Group Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedGroup.name}</h3>
                    {selectedGroup.description && (
                      <p className="text-sm text-gray-500 mt-1">{selectedGroup.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(selectedGroup)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      title={t('specialGroup.editTitle')}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedGroup.id, selectedGroup.name)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title={t('specialGroup.deleteTitle')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Members Header */}
              <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                <h4 className="text-sm font-medium text-gray-700">
                  {t('specialGroup.groupMembers', { count: groupMembers.length })}
                </h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsBulkModalOpen(true)}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <Upload className="w-4 h-4" />
                    <span>{t('specialGroup.bulkUpload')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsMemberModalOpen(true)
                      setSelectedUserIds([])
                      setSearchTerm('')
                    }}
                    className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>{t('specialGroup.addMember')}</span>
                  </button>
                </div>
              </div>

              {/* Members List */}
              {isLoadingGroupMembers ? (
                <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
              ) : groupMembers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {t('specialGroup.membersEmpty')}
                </div>
              ) : (
                <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {groupMembers.map((member: SpecialGroupMember) => (
                    <div key={member.userId} className="px-6 py-3 flex justify-between items-center hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">{member.fullName}</p>
                        <p className="text-sm text-gray-500">{member.email}</p>
                        {member.departmentName && (
                          <p className="text-xs text-gray-400">{member.departmentName}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.userId, member.fullName)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title={t('specialGroup.removeMemberTitle')}
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p>{t('specialGroup.selectPrompt')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) closeModal() }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? t('specialGroup.modal.editTitle') : t('specialGroup.modal.addTitle')}</DialogTitle>
          </DialogHeader>
          <form id="special-group-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('specialGroup.modal.nameLabel')}
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('specialGroup.modal.namePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('specialGroup.modal.descriptionLabel')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows={3}
                placeholder={t('specialGroup.modal.descriptionPlaceholder')}
              />
            </div>
          </form>
          <DialogFooter>
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="special-group-form"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {editingGroup ? t('common.edit') : t('common.add')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Modal */}
      <Dialog open={isMemberModalOpen && !!selectedGroup} onOpenChange={(open) => { if (!open) { setIsMemberModalOpen(false); setSelectedUserIds([]); setSearchTerm('') } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('specialGroup.addMemberModal.title', { name: selectedGroup?.name ?? '' })}</DialogTitle>
          </DialogHeader>
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('specialGroup.addMemberModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Selected Count */}
        {selectedUserIds.length > 0 && (
          <div className="mb-3 px-3 py-2 bg-primary-50 text-primary-700 rounded-lg text-sm">
            {t('specialGroup.addMemberModal.selectedCount', { count: selectedUserIds.length })}
          </div>
        )}

        {/* Available Members List */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
          {availableMembers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('specialGroup.addMemberModal.noAvailable')}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {availableMembers.map((member: Member) => (
                <label
                  key={member.userId}
                  className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(member.userId)}
                    onChange={() => toggleUserSelection(member.userId)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{member.fullName}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                    {member.departmentName && (
                      <p className="text-xs text-gray-400">{member.departmentName}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => { setIsMemberModalOpen(false); setSelectedUserIds([]); setSearchTerm('') }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAddMembers}
              disabled={selectedUserIds.length === 0 || addMembersMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {selectedUserIds.length > 0 ? t('specialGroup.addMemberModal.addCount', { count: selectedUserIds.length }) : t('specialGroup.addMemberModal.add')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={isBulkModalOpen && !!selectedGroup} onOpenChange={(open) => { if (!open) { setIsBulkModalOpen(false); setCsvFile(null) } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('specialGroup.bulkModal.title', { name: selectedGroup?.name ?? '' })}</DialogTitle>
          </DialogHeader>
        <div className="space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">{t('specialGroup.bulkModal.csvTitle')}</h3>
            <p className="text-sm text-blue-800 mb-3">
              {t('specialGroup.bulkModal.csvDescription')}
            </p>
            <p className="text-sm font-semibold text-blue-900 mb-1">{t('specialGroup.bulkModal.csvFormat')}</p>
            <p className="text-sm text-blue-800 font-mono bg-blue-100 rounded px-2 py-1">
              {t('specialGroup.bulkModal.csvEmailHeader')}<br />
              user1@company.com<br />
              user2@company.com
            </p>
            <div className="mt-3 text-xs text-blue-700">
              <p className="font-semibold mb-1">{t('specialGroup.bulkModal.csvNotes')}</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>{t('specialGroup.bulkModal.csvNote1')}</li>
                <li>{t('specialGroup.bulkModal.csvNote2')}</li>
                <li>{t('specialGroup.bulkModal.csvNote3')}</li>
              </ul>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('specialGroup.bulkModal.fileLabel')}
            </label>
            <label className="cursor-pointer">
              <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-gray-50 transition-colors">
                {csvFile ? (
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">{csvFile.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(csvFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      {t('specialGroup.bulkModal.fileClick')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('specialGroup.bulkModal.fileDrag')}
                    </p>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </div>
        </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => { setIsBulkModalOpen(false); setCsvFile(null) }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleBulkUpload}
              disabled={!csvFile || bulkProcessing}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {bulkProcessing ? t('specialGroup.bulkModal.submitting') : t('specialGroup.bulkModal.submit')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}


